import { createElement, Component } from 'react'
import { StyleSheet } from 'react-native'

// Polyfills the `window.ReactNativeWebView.postMessage` API that
// react-native-webview exposes inside its native WebView, so HTML written
// against the native API works unchanged in the web iframe.
//
// targetOrigin is the parent's origin captured here at module load (in
// the parent's context). We can't evaluate `window.location.origin` from
// inside the iframe because srcDoc iframes report it as 'null' in some
// Chrome versions, which would silently drop every message.
const TARGET_ORIGIN =
  typeof window !== 'undefined' && window.location ? window.location.origin : '*'

const REACT_NATIVE_WEBVIEW_POLYFILL = `
  window.ReactNativeWebView = {
    postMessage: function (data) { window.parent.postMessage(data, ${JSON.stringify(
      TARGET_ORIGIN,
    )}) }
  };
`

const injectScripts = (html: string, scripts: Array<string | undefined>) => {
  const body = scripts.filter(Boolean).join('\n')
  if (!body) {
    return html
  }
  const tag = `<script>${body}</script>`
  return html.includes('</body>') ? html.replace('</body>', `${tag}</body>`) : html + tag
}

// Initially copied from https://github.com/react-native-web-community/react-native-web-webview
// and rewritten as a generic shim. No f2chart-specific code lives here anymore.
export class WebView extends Component<any> {
  frameRef: HTMLIFrameElement | null = null

  componentDidMount() {
    if (this.props.onMessage) {
      window.addEventListener('message', this.onMessage, true)
    }
  }

  componentWillUnmount() {
    if (this.props.onMessage) {
      window.removeEventListener('message', this.onMessage, true)
    }
  }

  setRef = (ref: HTMLIFrameElement | null) => {
    this.frameRef = ref
  }

  reload = () => {
    this.frameRef?.contentWindow?.location.reload()
  }

  injectJavaScript = (script: string) => {
    const doc = this.frameRef?.contentWindow?.document
    if (!doc) {
      return
    }
    const element = doc.createElement('script')
    element.textContent = script
    doc.body.appendChild(element)
  }

  onMessage = (event: MessageEvent) => {
    // The browser sets `source`, so a sender can't spoof it — unlike `origin`
    // (which can be "null" for any sandboxed frame). Only accept messages
    // whose source is our iframe's contentWindow.
    if (!event.source || event.source !== this.frameRef?.contentWindow) {
      return
    }
    this.props.onMessage?.({ nativeEvent: { data: event.data } })
  }

  getSrcDoc(): string | undefined {
    const { source, injectedJavaScript } = this.props
    const html = source && typeof source === 'object' ? source.html : undefined
    if (!html) {
      return undefined
    }
    return injectScripts(html, [REACT_NATIVE_WEBVIEW_POLYFILL, injectedJavaScript])
  }

  getSrc(): string | undefined {
    const { source } = this.props
    if (source && typeof source === 'object' && 'uri' in source) {
      return source.uri
    }
    return undefined
  }

  render() {
    const { onLoadEnd, style } = this.props
    const flat = StyleSheet.flatten(style) as any

    return createElement('iframe', {
      ref: this.setRef,
      src: this.getSrc(),
      srcDoc: this.getSrcDoc(),
      width: flat?.width,
      height: flat?.height,
      style: StyleSheet.flatten([styles.iframe, style]),
      allowFullScreen: true,
      frameBorder: '0',
      scrolling: 'no',
      onLoad: onLoadEnd,
    })
  }
}

export default WebView

const styles = StyleSheet.create({
  iframe: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
    overflow: 'hidden',
  },
})
